/**
 * Гэрэл / харанхуй горим.
 *
 * ⚠️ CSS тал дээр `dark:` вариант НЭГ Ч БАЙХГҮЙ. `index.css` нь
 *    `:root[data-theme='dark']` дотор токенуудыг дарж бичдэг. Тиймээс
 *    энэ файлын ганц ажил бол `<html data-theme>`-д ТОДОРХОЙ утга
 *    ('light' | 'dark') тавих явдал.
 *
 * ⚠️ «Систем» сонголтыг CSS-д prefers-color-scheme-ээр давхардуулж
 *    ЗАСАХГҮЙ — JS энд шийдээд бичнэ. Хоёр газар шийдвэл гар аргаар
 *    сонгосон утга системийн утгатай зөрөх алдаа гарна.
 *
 * ⚠️ Анхны утгыг `index.html` дахь жижиг script paint-аас ӨМНӨ тавьдаг.
 *    Тэр script-ийн түлхүүр (`boots-theme`) ба энд байгаа нь ижил байх
 *    ёстой — салбал хуудас цагаанаар анивчина.
 */

import {
  createContext,
  use,
  useCallback,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';

export type ThemeMode = 'light' | 'dark' | 'system';
export type ResolvedTheme = 'light' | 'dark';

/** index.html дахь script-тэй ИЖИЛ байх ёстой. */
const STORAGE_KEY = 'boots-theme';

interface ThemeState {
  /** Хэрэглэгчийн сонголт */
  mode: ThemeMode;
  /** Бодитоор хэрэглэгдэж буй горим */
  resolved: ResolvedTheme;
  setMode: (m: ThemeMode) => void;
  /** Гэрэл ↔ харанхуй солино (систем байсан бол тодорхой утга болно) */
  toggle: () => void;
}

const ThemeContext = createContext<ThemeState | null>(null);

function readStored(): ThemeMode {
  try {
    const v = localStorage.getItem(STORAGE_KEY);
    if (v === 'light' || v === 'dark' || v === 'system') return v;
  } catch {
    // Private горим, эсвэл localStorage хаагдсан — системийн утгаар явна
  }
  return 'system';
}

function systemTheme(): ResolvedTheme {
  return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
}

export function ThemeProvider({ children }: { children: ReactNode }) {
  const [mode, setModeState] = useState<ThemeMode>(readStored);
  const [system, setSystem] = useState<ResolvedTheme>(systemTheme);

  // Үйлдлийн системийн сонголт хуудас нээлттэй байхад өөрчлөгдөж болно
  useEffect(() => {
    const mq = window.matchMedia('(prefers-color-scheme: dark)');
    const onChange = () => setSystem(mq.matches ? 'dark' : 'light');
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, []);

  const resolved: ResolvedTheme = mode === 'system' ? system : mode;

  useEffect(() => {
    document.documentElement.dataset.theme = resolved;
  }, [resolved]);

  const setMode = useCallback((m: ThemeMode) => {
    setModeState(m);
    try {
      localStorage.setItem(STORAGE_KEY, m);
    } catch {
      // Хадгалж чадаагүй ч тухайн сесс дотор ажиллана
    }
  }, []);

  const toggle = useCallback(() => {
    setModeState((prev) => {
      const next: ThemeMode =
        (prev === 'system' ? systemTheme() : prev) === 'dark' ? 'light' : 'dark';
      try {
        localStorage.setItem(STORAGE_KEY, next);
      } catch {
        // дээрхтэй ижил
      }
      return next;
    });
  }, []);

  const value = useMemo<ThemeState>(
    () => ({ mode, resolved, setMode, toggle }),
    [mode, resolved, setMode, toggle],
  );

  return <ThemeContext value={value}>{children}</ThemeContext>;
}

export function useTheme(): ThemeState {
  const ctx = use(ThemeContext);
  if (!ctx) throw new Error('useTheme нь ThemeProvider дотор дуудагдана');
  return ctx;
}
